import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, forkJoin, from, Observable, Subscription } from 'rxjs';
import { bufferCount, concatMap } from 'rxjs/operators';
import { Hourtype } from 'src/app/afas-classes/hourtype';
import { Settlement } from 'src/app/classes/settlement.class';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { PeriodService } from 'src/app/services/period.service';
import { SettlementsService } from 'src/app/services/settlements.service';
import { Utils } from 'src/app/utils.class';
import { EditSettlementDialogComponent } from './edit-settlement-dialog/edit-settlement-dialog.component';

@Component({
    selector: 'app-settlements-send',
    templateUrl: './settlements-send.component.html',
    styleUrls: ['./settlements-send.component.scss'],
    standalone: false,
})
export class SettlementsSendComponent extends CodaltComponent implements OnChanges, OnInit {
    @Input() hideDateSelection = false;
    @Input() beginDate: Date;
    @Input() endDate: Date;
    @Input() user: User;
    @Input() set hideSent(value: boolean) {
        this.fcHideSent.setValue(value);
    }
    allSettlements: Settlement[];
    hourtypes: Hourtype[];
    fcHideSent = new FormControl(false);
    fcShowOnlyMissing = new FormControl(false);
    fcEndDate = new FormControl();
    fromDate: Date;
    itemsToSend = 0;
    sentCount = 0;
    correctCount = 0;
    settlementsSubscription$: Subscription;
    excludedHourCodes = ['UITV_MIN2'];

    constructor(
        private dialog: MatDialog,
        private settlementsService: SettlementsService,
        private periodService: PeriodService
    ) {
        super();
    }

    ngOnInit() {
        this.fcEndDate.valueChanges.subscribe(endDate => {
            this.endDate = Utils.setTime(endDate, 23, 59);

            this.getData();
        });
    }

    ngOnChanges(): void {
        if (!this.fcEndDate.value) {
            this.fcEndDate.setValue(this.endDate);
        }

        this.getData();
    }

    private getData() {
        this.allSettlements = [];

        if (!this.user) {
            // Zelfde venster als de backend: periode van de einddatum plus de periode daarvoor.
            const { beginDate } = this.periodService.getPeriodDateInfo(
                this.periodService.getPeriodOfDate(this.endDate)
            );

            this.fromDate = this.periodService.getPeriodDateInfo(
                this.periodService.getPeriodOfDate(new Date(beginDate.getTime() - 1))
            ).beginDate;
        }

        const settlements$ = this.user
            ? this.settlementsService.getUserSettlements(this.beginDate, this.endDate, this.user.id)
            : this.settlementsService.getSettlementsTill(this.endDate);

        const hourtypes$ = this.settlementsService.getHourtypes();

        this.subscriptions.add(
            combineLatest([settlements$, hourtypes$]).subscribe(([settlements, hourtypes]) => {
                this.hourtypes = hourtypes.data;

                settlements.data.forEach(settlement => {
                    settlement.hourtype = this.hourtypes.find(h => h.code === settlement.hourtype_code);

                    settlement.missing_values = !(
                        settlement.afas_employee_id ??
                        settlement.realisation?.user?.afas_employee_id ??
                        settlement.user?.afas_employee_id
                    );
                });

                this.allSettlements = settlements.data
                    .filter(s => !this.excludedHourCodes.includes(s.hourtype_code))
                    .sort((a, b) =>
                        (a.bookdate + a.description + a.id).localeCompare(b.bookdate + b.description + b.id)
                    );

                this.calculateItemsToSend();
            })
        );
    }

    calculateItemsToSend() {
        this.itemsToSend = this.allSettlements.filter(
            s => !s.missing_values && !s.afas_ptrealization_id && s.minutes !== 0
        ).length;

        this.sentCount = this.allSettlements.filter(s => !!s.afas_ptrealization_id).length;

        this.correctCount = this.allSettlements.filter(s => !s.missing_values).length;
    }

    editSettlement(settlement: Settlement, editOnly: 'project' | 'hourtype') {
        if ((settlement.afas_error || !settlement.send_to_afas) && !settlement.afas_ptrealization_id) {
            this.dialog
                .open(EditSettlementDialogComponent, {
                    maxWidth: '500px',
                    width: '100vw',
                    maxHeight: '100%',
                    disableClose: false,
                    panelClass: 'comment-edit-dialog',
                    data: {
                        settlement,
                        bookdate: this.beginDate,
                        editOnly,
                    },
                })
                .afterClosed()
                .subscribe(() => {
                    this.getData();
                });
        }
    }

    sendAllToAfas() {
        const allSett$ = [];

        this.settlementsSubscription$ = new Subscription();

        this.allSettlements.forEach(settlement => {
            if (!settlement.missing_values && !settlement.afas_ptrealization_id) {
                allSett$.push(
                    new Observable(observer => {
                        settlement.afas_error = null;

                        settlement.afas_project_id =
                            settlement.afas_project_id ??
                            settlement.realisation?.afas_project_id ??
                            settlement.realisation?.planning?.afas_project_id ??
                            settlement.realisation?.user_planning?.afas_project_id;

                        settlement.afas_employee_id = settlement.afas_employee_id ?? settlement.user?.afas_employee_id;

                        settlement.sent_to_afas = null;

                        settlement.send_to_afas = new Date();

                        this.settlementsSubscription$.add(
                            this.settlementsService.sendSingle(settlement).subscribe({
                                next: response => {
                                    Object.assign(settlement, response.data);

                                    this.calculateItemsToSend();

                                    observer.complete();
                                },
                                // Een settlement die Afas weigert mag de rest van de batch niet blokkeren.
                                error: () => {
                                    observer.complete();
                                },
                            })
                        );
                    })
                );
            }
        });

        this.settlementsSubscription$.add(
            from(allSett$)
                .pipe(
                    bufferCount(2),
                    concatMap(b => forkJoin(b))
                )
                .subscribe({
                    complete: () => {
                        this.cancelSendToAfas();
                    },
                })
        );
    }

    cancelSendToAfas() {
        this.settlementsSubscription$.unsubscribe();

        this.settlementsSubscription$ = null;

        this.getData();
    }

    sendToAfas(settlement: Settlement) {
        settlement.afas_error = null;

        settlement.afas_project_id =
            settlement.afas_project_id ??
            settlement.realisation?.afas_project_id ??
            settlement.realisation?.planning?.afas_project_id ??
            settlement.realisation?.user_planning?.afas_project_id;

        settlement.afas_employee_id = settlement.afas_employee_id ?? settlement.user?.afas_employee_id;

        settlement.sent_to_afas = null;

        settlement.send_to_afas = new Date();

        this.settlementsService.sendSingle(settlement).subscribe(response => {
            Object.assign(settlement, response.data);

            this.calculateItemsToSend();
        });
    }
}
